/**
 * A one-shot hand-over of a social provider's outcome, for the SAME-TAB path.
 *
 * On a phone the provider owns the whole tab, so there is no popup to relay
 * through and one full page load is unavoidable. What IS avoidable is the
 * second hand-over: `/auth/social/callback` used to render the nine-field
 * billing address itself, on the auth layout, with no price, no order summary
 * and no configuration in sight — and then navigate again. Now it stashes the
 * outcome here and sends the customer straight back to the checkout, which
 * renders the same form with the total pinned above it.
 *
 * Why not the query string, given the ticket already travels in one on the
 * callback URL today: `gtag` sends `document.location` as `page_location` on
 * its automatic `page_view`, so `?register=<ticketId>` would be exported to
 * GA4 — and a Back-button replay of a single-use token produces a confusing
 * error rather than nothing. `sessionStorage` is same-origin, scoped to the
 * tab (the lifetime `useCheckoutDraft` already chose, for the same reasons),
 * consumed on read, and invisible to both analytics and the history stack.
 *
 * The redirect still carries a bare `?social=1` marker, so the checkout knows
 * to look without reading storage on every page load.
 */
const STORAGE_KEY = 'mw_social_handoff'

export type SocialHandoffParams = Record<string, string>

export function useSocialHandoff() {
  function stash(params: SocialHandoffParams): void {
    if (!import.meta.client) return
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(params))
    } catch {
      // Private mode, or storage disabled. The callback page's own handling is
      // the fallback, so a customer loses the shortcut and nothing else.
    }
  }

  /** Reads AND removes: the ticket inside is single-use, and so is this. */
  function take(): SocialHandoffParams | null {
    if (!import.meta.client) return null
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY)
      sessionStorage.removeItem(STORAGE_KEY)
      if (!raw) return null
      const parsed = JSON.parse(raw)
      if (!parsed || typeof parsed !== 'object') return null
      return parsed as SocialHandoffParams
    } catch {
      return null
    }
  }

  return { stash, take }
}
